import {
  ApolloProvider,
  DirectionProvider,
  RouterProvider,
  ToastContextProvider,
  TooltipPrimitiveProvider,
  apolloClient,
} from '@wirechunk/ui';
import type { FunctionComponent } from 'react';
import { Suspense } from 'react';
import { AnalyticsContextProvider } from './components/analytics-context-provider.tsx';
import { ErrorBoundary } from './components/error-boundary.tsx';
import { IntercomProvider } from './components/intercom-provider.tsx';
import { RootConditionalLogicDataContextProvider } from './components/root-conditional-logic-data-context-provider.tsx';
import { SessionUserProvider } from './components/session-user-provider.tsx';
import { ThemeProvider } from './components/theme-provider.tsx';
import { LazyStripeElementsProvider } from './lazy-stripe-elements-provider.tsx';
import { router } from './router.tsx';

const { admin, hasProducts } = window.wirechunk;

const WithSessionUser: FunctionComponent = () => (
  <RootConditionalLogicDataContextProvider>
    <AnalyticsContextProvider>
      <IntercomProvider>
        <RouterProvider router={router} />
      </IntercomProvider>
    </AnalyticsContextProvider>
  </RootConditionalLogicDataContextProvider>
);

const WithStripe: FunctionComponent = () =>
  hasProducts ? (
    <SessionUserProvider>
      <WithSessionUser />
    </SessionUserProvider>
  ) : (
    <WithSessionUser />
  );

export const App: FunctionComponent = () => (
  <ErrorBoundary showDialog>
    <TooltipPrimitiveProvider delayDuration={200}>
      <DirectionProvider dir="ltr">
        <ThemeProvider>
          <ToastContextProvider>
            <ApolloProvider client={apolloClient}>
              {admin ? (
                <WithStripe />
              ) : (
                <Suspense>
                  <LazyStripeElementsProvider>
                    <WithStripe />
                  </LazyStripeElementsProvider>
                </Suspense>
              )}
            </ApolloProvider>
          </ToastContextProvider>
        </ThemeProvider>
      </DirectionProvider>
    </TooltipPrimitiveProvider>
  </ErrorBoundary>
);
